import { useState, useEffect, useCallback } from "react";
import { Mail, Send, Users, AlertTriangle, History } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const SEGMENTS = [
  ["all", "All customers"], ["active", "Active only"], ["suspended", "Suspended"],
  ["kyc_approved", "KYC approved"], ["kyc_pending", "KYC pending"],
];

export default function AdminEmail() {
  const [configured, setConfigured] = useState(true);
  const [form, setForm] = useState({ segment: "all", subject: "", body: "" });
  const [count, setCount] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [testTo, setTestTo] = useState("");

  const loadCampaigns = useCallback(() => api.get("/admin/email/campaigns").then((r) => setCampaigns(r.data)), []);
  useEffect(() => {
    api.get("/admin/integrations/status").then((r) => setConfigured(r.data.email)).catch(() => {});
    loadCampaigns();
  }, [loadCampaigns]);
  useEffect(() => { api.get("/admin/email/audience", { params: { segment: form.segment } }).then((r) => setCount(r.data.count)).catch(() => {}); }, [form.segment]);

  const send = async () => {
    setConfirm(false);
    try {
      const { data } = await api.post("/admin/email/send", form);
      if (data.campaign?.status === "sent") toast.success(`Sent to ${data.campaign.sent_count} recipients`);
      else if (data.campaign?.status === "not_configured") toast.warning("Saved — but email provider is not configured yet");
      else toast.error("Send failed");
      loadCampaigns();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a test email"); return; }
    try { const { data } = await api.post("/admin/email/send", { ...form, test_to: testTo }); toast[data.ok ? "success" : "warning"](data.ok ? "Test email sent" : "Provider not configured — nothing sent"); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      {!configured && (
        <div className="rounded-xl bg-gold/10 border border-gold/30 text-gold p-4 flex items-center gap-2 text-sm" data-testid="email-not-configured">
          <AlertTriangle className="h-4 w-4" /> Email provider not configured. Add a <b>RESEND_API_KEY</b> to send real emails. You can still compose and preview; campaigns will be saved but not delivered.
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-fx-s1 border border-white/5 p-5 space-y-4">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-brandblue" /><h2 className="font-display font-bold">Compose email</h2></div>
          <div className="space-y-1">
            <Label className="text-xs">Audience</Label>
            <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
              <SelectTrigger data-testid="email-segment" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-fx-s2 border-white/10 text-white">{SEGMENTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-xs text-fx-s3 flex items-center gap-1 pt-1"><Users className="h-3.5 w-3.5" /> {count} recipients</div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Subject</Label><Input data-testid="email-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-fx-base border-white/10" /></div>
          <div className="space-y-1"><Label className="text-xs">Message</Label><Textarea data-testid="email-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-fx-base border-white/10 min-h-[160px]" /></div>
          <div className="flex gap-2">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="bg-fx-base border-white/10 h-9" />
            <Button onClick={sendTest} data-testid="email-test-btn" variant="outline" className="border-white/10 bg-fx-base shrink-0 h-9">Send test</Button>
          </div>
          <Button onClick={() => { if (!form.subject || !form.body) { toast.error("Subject and message required"); return; } setConfirm(true); }} data-testid="email-send-btn" className="w-full bg-brandblue hover:bg-blue-500 gap-2"><Send className="h-4 w-4" /> Send to {count} customers</Button>
        </div>
        <div className="rounded-xl bg-fx-s1 border border-white/5 p-5">
          <div className="text-xs uppercase tracking-wider text-fx-s3 mb-2">Preview</div>
          <div className="rounded-lg bg-white text-slate-900 p-4 min-h-[200px]">
            <div className="font-bold text-lg border-b border-slate-200 pb-2 mb-3">{form.subject || "Subject preview"}</div>
            <div className="text-sm whitespace-pre-line leading-relaxed">{form.body || "Your message preview appears here."}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2"><History className="h-4 w-4 text-brandblue" /><h2 className="font-display font-bold">Campaign History</h2></div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
            <th className="px-4 py-3">Subject</th><th className="px-4 py-3">Segment</th><th className="px-4 py-3">Recipients</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Sent by</th><th className="px-4 py-3">When</th>
          </tr></thead>
          <tbody>
            {campaigns.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-fx-s3">No campaigns yet.</td></tr>}
            {campaigns.map((c) => (
              <tr key={c.id} data-testid={`email-campaign-${c.id}`} className="border-b border-white/[0.03]">
                <td className="px-4 py-3 font-medium">{c.subject}</td>
                <td className="px-4 py-3 text-fx-s3 text-xs">{c.segment}</td>
                <td className="px-4 py-3 font-mono">{c.sent_count}/{c.recipient_count}</td>
                <td className="px-4 py-3"><span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono", c.status === "sent" ? "text-pos bg-pos/15" : c.status === "not_configured" ? "text-gold bg-gold/15" : "text-neg bg-neg/15")}>{c.status.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-xs text-fx-s3">{c.sender_email}</td>
                <td className="px-4 py-3 text-xs text-fx-s3">{timeAgo(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="email-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display">Confirm send</DialogTitle></DialogHeader>
          <p className="text-sm text-fx-s3">You are about to email <b className="text-white">{count}</b> customers ({SEGMENTS.find((s) => s[0] === form.segment)?.[1]}). This cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button onClick={() => setConfirm(false)} variant="outline" className="border-white/10 bg-fx-base">Cancel</Button>
            <Button onClick={send} data-testid="email-confirm-send" className="bg-brandblue hover:bg-blue-500 gap-2"><Send className="h-4 w-4" /> Send now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
